// WeglotProvider.tsx
import React, { createContext, useEffect, useMemo, useState } from 'react'

declare global {
  interface Window {
    Weglot: {
      initialize: (opts: { api_key: string }) => void
      destroy?: () => void
      switchTo?: (lang: string) => void
      getCurrentLang?: () => string
      getInitialLang?: () => string
      on?: (
        event: 'languageChanged',
        cb: (newLang: string, prevLang: string) => void
      ) => void
      off?: (
        event: 'languageChanged',
        cb: (newLang: string, prevLang: string) => void
      ) => void
    }
  }
}

type WeglotContextValue = {
  ready: boolean
  currentLang?: string
  switchTo: (lang: string) => void
}

export const WeglotContext = createContext<WeglotContextValue | undefined>(
  undefined
)

interface Props {
  apiKey?: string
  children?: React.ReactNode
  disabled?: boolean
}

export const WeglotProvider: React.FC<Props> = ({
  apiKey,
  children,
  disabled,
}) => {
  const [ready, setReady] = useState(false)
  const [currentLang, setCurrentLang] = useState<string>()

  useEffect(() => {
    if (typeof window === 'undefined' || disabled) return

    const init = () => {
      if (!apiKey) return
      window.Weglot.initialize({ api_key: apiKey })
      setTimeout(() => {
        setCurrentLang(window.Weglot.getCurrentLang?.())
      }, 500)
      setReady(true)
    }

    // already loaded?
    if (window.Weglot) {
      init()
      return
    }

    const script = document.createElement('script')
    script.src = 'https://cdn.weglot.com/weglot.min.js'
    script.async = true
    script.onload = init
    document.head.appendChild(script)

    return () => {
      window.Weglot?.destroy?.()
      document.head.removeChild(script)
    }
  }, [apiKey, disabled])

  useEffect(() => {
    if (!ready || !window.Weglot?.on || disabled) return

    const handler = (newLang: string) => {
      setCurrentLang(newLang)
    }

    window.Weglot.on('languageChanged', handler)

    return () => {
      window.Weglot?.off?.('languageChanged', handler)
    }
  }, [ready, disabled])

  const ctx = useMemo<WeglotContextValue>(
    () => ({
      ready,
      currentLang,
      switchTo: (lang: string) => {
        if (ready && window.Weglot?.switchTo) window.Weglot.switchTo(lang)
      },
    }),
    [ready, currentLang]
  )

  return (
    <WeglotContext.Provider value={ctx}>
      {children ?? null}
    </WeglotContext.Provider>
  )
}
